import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Card, message, Upload, Radio, Alert } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import api from '../../api/axios';
import useSettingsStore from '../../store/settingsStore';

const AppSettingsTab = () => {
  const [form] = Form.useForm();
  const { appName, appSlogan, logoUrl, setSettings, fetchSettings } = useSettingsStore();
  const [uploadLoading, setUploadLoading] = useState(false);

  // Varsayilan sifre modu. Store'daki genel ayarlardan DEGIL, /settings
  // ucundan geliyor - bu alan yalnizca yoneticilere donuyor.
  const [passwordMode, setPasswordMode] = useState(null);
  const [passwordEnvSet, setPasswordEnvSet] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [sprayMode, setSprayMode] = useState(null);
  const [spraySaving, setSpraySaving] = useState(false);

  useEffect(() => {
    form.setFieldsValue({ app_name: appName, app_slogan: appSlogan });
  }, [appName, appSlogan, form]);

  useEffect(() => {
    api.get('/settings')
      .then(res => {
        setPasswordMode(res.data.default_password_mode || 'random');
        setPasswordEnvSet(!!res.data.default_password_env_set);
        setSprayMode(res.data.login_spray_mode || 'engelle');
      })
      .catch(() => {});
  }, []);

  // ACİL DURUM VALFİ. Koruma doğrudan giriş ucuna dokunuyor ve eşikler
  // ölçüme dayansa da beklenmedik bir trafik deseni çıkarsa (kurum tek
  // bir VPN'in arkasına geçerse gibi) yöneticinin deploy beklemeden
  // kapatabilmesi gerekiyor.
  const handleSprayModeChange = async (mode) => {
    const onceki = sprayMode;
    setSprayMode(mode);
    setSpraySaving(true);
    try {
      await api.put('/settings', { login_spray_mode: mode });
      message.success('Giriş koruması ayarı kaydedildi.');
    } catch {
      setSprayMode(onceki);
      message.error('Ayar kaydedilemedi.');
    } finally {
      setSpraySaving(false);
    }
  };

  const handlePasswordModeChange = async (mode) => {
    const onceki = passwordMode;
    setPasswordMode(mode);
    setPasswordSaving(true);
    try {
      await api.put('/settings', { default_password_mode: mode });
      message.success('Varsayılan şifre ayarı kaydedildi.');
    } catch {
      setPasswordMode(onceki);
      message.error('Ayar kaydedilemedi.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSave = async (values) => {
    try {
      await api.put('/settings', values);
      setSettings(values);
      message.success('Ayarlar kaydedildi.');
    } catch {
      message.error('Ayarlar kaydedilemedi.');
    }
  };

  const handleLogoUpload = async ({ file }) => {
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await api.post('/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Store'u güncelle
      useSettingsStore.setState({ logoUrl: res.data.logo_url });
      // Favicon güncelle (cache busting için timestamp)
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = res.data.logo_url + '?t=' + Date.now();
      message.success('Logo güncellendi.');
    } catch {
      message.error('Logo yüklenemedi.');
    } finally {
      setUploadLoading(false);
    }
    return false; // antd'nin otomatik yüklemesini engelle
  };

  return (
    <Card title="Uygulama Ayarları" className="max-w-lg">
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Form.Item
          label="Uygulama Adı"
          name="app_name"
          rules={[{ required: true, message: 'Uygulama adı gerekli' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Slogan" name="app_slogan">
          <Input />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit">Kaydet</Button>
        </Form.Item>
      </Form>

      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 24, marginTop: 8 }}>
        <p style={{ fontWeight: 600, marginBottom: 12 }}>Logo / Favicon</p>
        {logoUrl && (
          <div style={{ marginBottom: 12 }}>
            <img src={logoUrl} alt="Logo" style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 8, border: '1px solid #e8e8e8', padding: 4, display: 'block' }} />
          </div>
        )}
        <Upload
          accept=".png,.jpg,.jpeg,.svg,.ico"
          showUploadList={false}
          beforeUpload={(file) => { handleLogoUpload({ file }); return false; }}
        >
          <Button icon={<UploadOutlined />} loading={uploadLoading}>
            {logoUrl ? 'Logoyu Değiştir' : 'Logo Yükle'}
          </Button>
        </Upload>
        <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
          PNG, JPG, SVG veya ICO. Maks. 512 KB. Tarayıcı sekmesinde favicon olarak görünür.
        </p>
      </div>

      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 24, marginTop: 24 }}>
        <p style={{ fontWeight: 600, marginBottom: 4 }}>Yeni Hesap Şifresi</p>
        <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
          <b>Yeni bir kullanıcı oluşturulduğunda</b> hangi şifrenin atanacağını
          belirler; şifre işlem bitince ekranda gösterilir.
          <br />
          <b>Şifre sıfırlamayı etkilemez</b> — sıfırlama her zaman sunucu
          ayarlarındaki sabit şifreyi verir, çünkü o an kişi zaten "giremiyorum"
          diyordur ve şifreyi kendisine siz iletirsiniz.
        </p>

        <Radio.Group
          value={passwordMode}
          disabled={passwordMode === null || passwordSaving}
          onChange={(e) => handlePasswordModeChange(e.target.value)}
        >
          <Radio value="random" style={{ display: 'block', marginBottom: 8 }}>
            Her hesaba rastgele şifre üret
            <span style={{ color: '#52c41a', fontSize: 12, marginLeft: 6 }}>(önerilen)</span>
          </Radio>
          <Radio value="fixed" style={{ display: 'block' }}>
            Sabit şifre kullan
            <span style={{ color: '#888', fontSize: 12, marginLeft: 6 }}>
              (sunucu ayarlarında tanımlı değer)
            </span>
          </Radio>
        </Radio.Group>

        {passwordMode === 'fixed' && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message="Sabit şifre herkesçe bilinir hale gelir"
            description={
              passwordEnvSet
                ? 'Sistemde hesabı olan herkes bu şifreyi öğrenir. Hiç giriş yapılmamış bir hesap, sahibi ilk girişini yapana kadar bu şifreyle açık kalır. Kısa süreli kullanın; kişi giriş yapar yapmaz şifresini değiştirmesi gerekir.'
                : 'Sunucu ayarlarında sabit bir şifre tanımlı DEĞİL, bu yüzden yine rastgele şifre üretilecek.'
            }
          />
        )}
      </div>

      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 24, marginTop: 24 }}>
        <p style={{ fontWeight: 600, marginBottom: 4 }}>Şifre Püskürtme Koruması</p>
        <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
          Bir internet adresinden kısa sürede <b>çok sayıda farklı hesaba</b>
          {' '}başarısız giriş denenirse devreye girer. Tek bir hesaba yapılan
          denemeler buraya girmez; onu hesap kilidi zaten durduruyor.
          <br />
          <b>Hiçbir hesap kilitlenmez</b> — yalnızca o adresten 15 dakika
          boyunca yeni giriş yapılamaz. Açık oturumlar çalışmaya devam eder.
        </p>

        <Radio.Group
          value={sprayMode}
          disabled={sprayMode === null || spraySaving}
          onChange={(e) => handleSprayModeChange(e.target.value)}
        >
          <Radio value="engelle" style={{ display: 'block', marginBottom: 8 }}>
            Uyar ve engelle
            <span style={{ color: '#52c41a', fontSize: 12, marginLeft: 6 }}>(önerilen)</span>
          </Radio>
          <Radio value="uyar" style={{ display: 'block', marginBottom: 8 }}>
            Yalnızca uyar
            <span style={{ color: '#888', fontSize: 12, marginLeft: 6 }}>
              (bildirim gelir, giriş kapanmaz)
            </span>
          </Radio>
          <Radio value="kapali" style={{ display: 'block' }}>
            Kapalı
          </Radio>
        </Radio.Group>

        {sprayMode === 'kapali' && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message="Koruma tamamen kapalı"
            description="Çok sayıda hesaba yayılan giriş denemeleri artık ne engellenir ne de size bildirilir. Bir sorunu geçici olarak aşmak için kapattıysanız, çözüldüğünde geri açmayı unutmayın."
          />
        )}
      </div>
    </Card>
  );
};

export default AppSettingsTab;
